import { useAppDispatch, useAppSelector } from "@app/hooks"
import { ArmedConfirmDialog } from "@cmp/armedConfirmDialog"
import { offboardStudentPopupClosed } from "@features/program/programSlice"
import { selectStudent } from "@features/program/programSliceSelectors"
import { unregisterStudent } from "@features/program/programSliceThunks"

// This action is a DROPOUT. It calls campus `user/school/dropout` — not the
// remove-from-program primitive — so it does considerably more than the old
// subtitle ("This removes them from the program") admitted: it records a dropout,
// moves the student's HubSpot enrollment into the DROPOUT pipeline stage (which
// is what churn reporting and HubSpot workflows read), removes them from the
// program's Slack channels, and notifies the external apps.
//
// So it says so, in full, and sits behind an armed countdown. A staff member
// should not be able to mark a student as churned by clicking through a dialog
// they have seen a hundred times.
function OffboardStudentPopup() {
  const dispatch = useAppDispatch()
  const model = useAppSelector(
    (state) => state.program.studentsPage.offboardStudentPopup,
  )
  const student = useAppSelector(
    selectStudent(model?.programId, model?.classId, model?.studentId),
  )
  const studentName = student
    ? `${student.firstName} ${student.lastName}`
    : "this student"

  return (
    <ArmedConfirmDialog
      open={model !== undefined}
      title={`Drop out ${studentName}?`}
      message={`This records ${studentName} as a DROPOUT — it is not the same as removing them from a program. To take them out of one program and leave the rest untouched, use Manage programs instead.`}
      consequences={[
        {
          text: "Ends this program's registration and its course classes",
          variant: "internal",
        },
        {
          text: "Marks this program's HubSpot enrollment as DROPOUT (churn reporting)",
          variant: "external",
        },
        {
          // NOT just this program's channels: campus calls
          // removeDroppedUserFromSlack, which removes the user from the Slack
          // WORKSPACE and deletes our record of their Slack identity. A student
          // still enrolled in another program loses Slack entirely.
          text: "Removes them from the Slack workspace entirely, not just this program's channels",
          variant: "external",
        },
        {
          text: "Removes their DataCamp account",
          variant: "external",
        },
        {
          text: "Marks them dropped out in analytics, across all their programs",
          variant: "external",
        },
      ]}
      footer="Dropping out is recorded against the student. It cannot be undone from here."
      okLabel="Drop out student"
      loading={model?.state === "pending"}
      failed={model?.state === "rejected"}
      onConfirm={() => {
        if (!model) {
          return
        }
        dispatch(
          unregisterStudent({
            programId: model.programId,
            classId: model.classId,
            studentId: model.studentId,
          }),
        )
      }}
      onCancel={() => dispatch(offboardStudentPopupClosed())}
    />
  )
}

export default OffboardStudentPopup
